import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PrivacyScoreDial from "@/components/PrivacyScoreDial";
import { toast } from "sonner";
import { Mail, AlertTriangle, Gavel, CheckCircle2, Clock, ExternalLink, FileText, ChevronRight, Activity, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STATUS_MAP = {
  sent: { label: "Enviado", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", icon: Mail },
  dry_run: { label: "Dry-run (admin)", color: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200", icon: Clock },
  preview: { label: "Preview", color: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200", icon: Clock },
  no_data_likely: { label: "Pendiente", color: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200", icon: Clock },
  escalated: { label: "Escalado", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", icon: AlertTriangle },
  deleted: { label: "Eliminado", color: "text-green-600", bg: "bg-green-50", border: "border-green-200", icon: CheckCircle2 },
  no_data_confirmed: { label: "Sin datos confirmado", color: "text-green-600", bg: "bg-green-50", border: "border-green-200", icon: CheckCircle2 },
  oposicion_confirmed: { label: "Marketing detenido", color: "text-green-600", bg: "bg-green-50", border: "border-green-200", icon: CheckCircle2 },
  refused: { label: "Rechazado", color: "text-red-600", bg: "bg-red-50", border: "border-red-200", icon: X },
  denuncia_ready: { label: "Denuncia APDP lista", color: "text-red-600", bg: "bg-red-50", border: "border-red-200", icon: Gavel },
};

const EVENT_LABEL = {
  request_sent: "Solicitud enviada",
  escalated: "Escalada formal",
  deleted: "Confirmación de eliminación",
  no_data_confirmed: "Sin datos confirmado",
  oposicion_confirmed: "Oposición al marketing confirmada",
  refused: "Responsable rechazó",
  denuncia_ready: "Denuncia lista",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("brokers"); // brokers | empresas

  const load = async () => {
    try { const { data } = await api.get("/dashboard"); setData(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };

  useEffect(() => { load(); }, []);

  const dispatchAgain = async () => {
    try { await api.post("/requests/dispatch"); await load(); toast.success("Solicitudes actualizadas"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const escalate = async (id) => {
    await api.post(`/requests/${id}/escalate`); await load(); toast.success("Recordatorio formal enviado");
  };
  const denuncia = async (id) => {
    await api.post(`/requests/${id}/denuncia`); await load(); toast.success("Denuncia APDP lista");
  };

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-24 font-mono text-sm text-gray-500">Cargando dashboard…</div>;

  return (
    <div className="bg-[#F9F9F8] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">

        {/* Header strip */}
        <div className="grid lg:grid-cols-12 gap-6 mb-8">
          <div className="lg:col-span-4 bg-white border border-gray-300 p-6 flex items-center gap-6" data-testid="dashboard-score">
            <PrivacyScoreDial score={data.score} size={150} />
            <div>
              <div className="eyebrow mb-1">Privacy Score</div>
              <h1 className="font-display text-2xl font-bold text-gray-900 leading-tight">Tu protección<br/>en tiempo real.</h1>
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            <Stat label="Brokers" value={data.stats.brokers_total} color="text-gray-900" testid="stat-brokers" />
            <Stat label="Con datos" value={data.stats.with_data} color="text-red-600" testid="stat-with-data" />
            <Stat label="Empresas" value={data.stats.empresas_total || 0} color="text-gray-900" testid="stat-empresas" />
            <Stat
              label="Spam detenido"
              value={data.stats.spam_stopped || 0}
              color={data.stats.spam_stopped > 0 ? "text-green-600" : "text-gray-400"}
              testid="stat-spam-stopped"
            />
          </div>
        </div>

        {/* Scans remaining strip — visible after the user has paid */}
        {(data.user?.paid_at || data.user?.subscribed) && (
          <div className="bg-white border border-gray-300 p-5 flex items-center justify-between mb-8" data-testid="scans-remaining-strip">
            <div>
              <div className="eyebrow mb-1">Escaneos gratuitos disponibles</div>
              <div className="font-mono text-2xl font-bold text-gray-900">
                {data.user?.scans_remaining ?? 5} <span className="text-sm text-gray-500 font-normal">de 5 RUT adicionales</span>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Puedes escanear el RUT de familia o socios sin costo. Para activar la limpieza completa en otro RUT, se requiere un nuevo pago único de $39.990.
              </p>
            </div>
            <a href="/" data-testid="scan-another-cta"
              className="bg-[#0B2447] hover:bg-[#071932] text-white px-5 py-2.5 text-sm font-semibold whitespace-nowrap">
              Escanear otro RUT
            </a>
          </div>
        )}

        {/* User identity strip */}
        <div className="bg-white border border-gray-300 p-5 flex items-center justify-between mb-8">
          <div>
            <div className="eyebrow mb-1">Titular</div>
            <div className="font-display text-lg font-semibold text-gray-900">{data.user.nombre_completo || data.user.nombre} · <span className="font-mono text-base text-gray-600">{data.user.rut || "—"}</span></div>
          </div>
          <button onClick={dispatchAgain} data-testid="btn-dispatch"
            className="text-sm border border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition-colors px-4 py-2 flex items-center gap-2">
            <Activity className="w-4 h-4" /> Re-escanear / Generar solicitudes
          </button>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Brokers grid */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="inline-flex border border-gray-300 bg-white" data-testid="dashboard-tabs">
                <button
                  onClick={() => setTab("brokers")}
                  data-testid="tab-brokers"
                  className={`px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold ${tab === "brokers" ? "bg-[#0B2447] text-white" : "text-gray-700 hover:bg-gray-50"}`}
                >
                  Brokers ({data.stats.brokers_total || 0})
                </button>
                <button
                  onClick={() => setTab("empresas")}
                  data-testid="tab-empresas"
                  className={`px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold ${tab === "empresas" ? "bg-[#0B2447] text-white" : "text-gray-700 hover:bg-gray-50"}`}
                >
                  Empresas ({data.stats.empresas_total || 0})
                </button>
              </div>
              <div className="font-mono text-xs text-gray-500">
                {(() => {
                  const list = data.requests.filter((r) => (r.entity_type || "broker") === (tab === "brokers" ? "broker" : "empresa"));
                  return `${list.length} solicitudes`;
                })()}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
              {(() => {
                const list = data.requests.filter((r) => (r.entity_type || "broker") === (tab === "brokers" ? "broker" : "empresa"));
                if (list.length === 0) {
                  return (
                    <div className="bg-white p-12 text-center sm:col-span-2" data-testid="empty-list">
                      <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600">
                        {tab === "brokers"
                          ? "Aún no se han generado solicitudes a brokers. Pulsa Re-escanear."
                          : "No has seleccionado empresas. Ve a tu perfil para agregarlas."}
                      </p>
                    </div>
                  );
                }
                return list.map((r) => {
                  const s = STATUS_MAP[r.status] || STATUS_MAP.sent;
                  const Icon = s.icon;
                  const evStatus = r.scan_status || r.evidence?.status;
                  const isEmpresa = (r.entity_type || "broker") === "empresa";
                  return (
                    <button key={r.id} onClick={() => setSelected(r)} data-testid={`broker-card-${r.broker_slug}`}
                      className="bg-white p-5 text-left hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-display font-semibold text-gray-900">{r.broker_name}</div>
                          <div className="text-xs font-mono text-gray-500 mt-1">
                            {isEmpresa
                              ? `${r.category || "Empresa"} · Presunción del titular`
                              : `Tier ${r.tier} · ${evStatus ? `escaneo: ${evStatus}` : (r.has_data ? "Datos detectados" : "Sin datos probable")}`}
                          </div>
                        </div>
                        <span className={`text-[10px] uppercase tracking-[0.15em] font-bold ${s.color}`}>{s.label}</span>
                      </div>
                      <div className={`flex items-center justify-between border-t ${s.border} pt-3 mt-3`}>
                        <div className={`flex items-center gap-2 text-xs ${s.color}`}><Icon className="w-3.5 h-3.5" />Plazo {new Date(r.deadline).toLocaleDateString("es-CL")}</div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          {/* Timeline */}
          <aside className="lg:col-span-4 bg-white border border-gray-300 p-6 h-fit sticky top-20">
            <div className="eyebrow mb-4">Línea de tiempo</div>
            <div className="space-y-4 max-h-[600px] overflow-auto pr-2" data-testid="timeline">
              {data.timeline.length === 0 && <div className="text-sm text-gray-500 font-mono">Sin actividad aún.</div>}
              {data.timeline.map((t, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-2 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm text-gray-900">{EVENT_LABEL[t.event] || t.event}</div>
                    <div className="text-xs text-gray-500">{t.broker_name} · {new Date(t.at).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" })}</div>
                    <div className="text-xs text-gray-600 mt-1">{t.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>

      {/* Side detail panel */}
      <AnimatePresence>
        {selected && (
          <RequestDrawer r={selected} onClose={() => setSelected(null)} onEscalate={escalate} onDenuncia={denuncia} />
        )}
      </AnimatePresence>
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="eyebrow mb-2">{label}</div>
      <div className={`font-display text-4xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function RequestDrawer({ r, onClose, onEscalate, onDenuncia }) {
  const s = STATUS_MAP[r.status] || STATUS_MAP.sent;
  const publicLink = `${window.location.origin}/broker/respond/${r.token}`;
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/40 z-40" />
      <motion.aside
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30 }}
        className="fixed top-0 right-0 bottom-0 w-full max-w-xl bg-white z-50 overflow-auto border-l border-gray-200"
        data-testid="request-drawer">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="eyebrow mb-1">Solicitud</div>
            <h2 className="font-display text-xl font-bold">{r.broker_name}</h2>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="p-2 hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Cell label="Estado"><span className={`font-bold ${s.color}`}>{s.label}</span></Cell>
            <Cell label="Tier">Tier {r.tier}</Cell>
            <Cell label="Plazo legal">{new Date(r.deadline).toLocaleDateString("es-CL")}</Cell>
            <Cell label="Escaneo real">{r.scan_status || "—"}</Cell>
          </div>

          {r.evidence && r.evidence.url && (
            <div>
              <div className="eyebrow mb-2">Evidencia del escaneo</div>
              <div className="bg-gray-50 border border-gray-200 p-3 text-xs space-y-1">
                <div><span className="text-gray-500">URL probada:</span> <a href={r.evidence.url} target="_blank" rel="noreferrer" className="font-mono text-blue-600 hover:underline break-all">{r.evidence.url}</a></div>
                {r.evidence.http_status != null && <div><span className="text-gray-500">HTTP:</span> <span className="font-mono">{r.evidence.http_status}</span> {r.evidence.response_time_ms != null && <span className="text-gray-500">· {r.evidence.response_time_ms}ms</span>}</div>}
                {r.evidence.notes && <div className="text-gray-600 italic mt-2">{r.evidence.notes}</div>}
                {r.evidence.snippet && <div className="mt-2 text-gray-700 line-clamp-3">{r.evidence.snippet}</div>}
              </div>
            </div>
          )}

          {r.evidence?.extracted_data && (
            <div>
              <div className="eyebrow mb-2 text-red-700">Datos personales extraídos del broker</div>
              <div className="bg-red-50 border-l-2 border-red-500 p-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {Object.entries(r.evidence.extracted_data).map(([k, v]) => (
                  <div key={k} className="flex gap-2 items-baseline">
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-red-700/70 shrink-0 w-16">{k}</span>
                    <span className="text-gray-900 font-mono break-all">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!r.evidence?.extracted_data && r.evidence?.exposed_fields && (
            <div>
              <div className="eyebrow mb-2 text-red-700">Campos expuestos en este broker</div>
              <div className="bg-red-50 border-l-2 border-red-500 p-4">
                <div className="flex flex-wrap gap-1.5">
                  {r.evidence.exposed_fields.map((label) => (
                    <span key={label} className="text-[10px] uppercase tracking-[0.12em] font-bold bg-white border border-red-300 text-red-700 px-2 py-1">
                      {label}
                    </span>
                  ))}
                </div>
                <div className="mt-3 text-xs text-red-700/80 italic">
                  Activa tu suscripción para ver los valores exactos de tus datos en este broker.
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="eyebrow mb-2">Link público del formulario broker</div>
            <div className="font-mono text-xs bg-gray-50 border border-gray-200 p-3 break-all flex items-center gap-2">
              <span className="flex-1">{publicLink}</span>
              <a href={publicLink} target="_blank" rel="noreferrer" data-testid="drawer-open-link"><ExternalLink className="w-3.5 h-3.5" /></a>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-2">Email ARCOP enviado</div>
            <div className="bg-gray-50 border border-gray-200 p-4 text-xs font-mono whitespace-pre-wrap leading-relaxed max-h-72 overflow-auto">
              <div className="font-bold mb-2">Asunto: {r.email_preview.subject}</div>
              <div className="text-gray-500 mb-3">Para: {r.email_preview.to}</div>
              {r.email_preview.body}
            </div>
            {r.delivery && (
              <div className="mt-2 text-xs flex items-center gap-2" data-testid={`delivery-status-${r.broker_slug}`}>
                {r.delivery.status === "sent" && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 border border-emerald-300 text-emerald-700 font-bold uppercase tracking-wider text-[10px]">
                    ✓ Entregado via Resend
                    {r.delivery.message_id && <span className="font-mono normal-case text-[9px] opacity-70">· id {String(r.delivery.message_id).slice(0,8)}</span>}
                  </span>
                )}
                {r.delivery.status === "failed" && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-red-50 border border-red-300 text-red-700 font-bold uppercase tracking-wider text-[10px]">
                    ✗ Envío falló
                    <span className="font-normal normal-case text-[10px]">{r.delivery.error}</span>
                  </span>
                )}
                {r.delivery.status === "preview_only" && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 border border-amber-300 text-amber-700 font-bold uppercase tracking-wider text-[10px]">
                    ⚠ Solo preview (Resend no configurado)
                  </span>
                )}
                {r.delivery.status === "no_data_skipped" && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 border border-gray-300 text-gray-600 font-bold uppercase tracking-wider text-[10px]">
                    — Sin envío (sin datos detectados)
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {!["deleted","no_data_confirmed","denuncia_ready"].includes(r.status) && (
              <button onClick={() => onEscalate(r.id)} data-testid="drawer-escalate"
                className="flex-1 border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white px-4 py-2 text-sm transition-colors">
                Escalar formalmente
              </button>
            )}
            {r.status !== "denuncia_ready" && r.status !== "deleted" && r.status !== "no_data_confirmed" && (
              <button onClick={() => onDenuncia(r.id)} data-testid="drawer-denuncia"
                className="flex-1 border border-red-600 text-red-700 hover:bg-red-600 hover:text-white px-4 py-2 text-sm transition-colors">
                Generar denuncia APDP
              </button>
            )}
          </div>
        </div>
      </motion.aside>
    </>
  );
}

function Cell({ label, children }) {
  return (
    <div className="border border-gray-200 p-3">
      <div className="eyebrow mb-1">{label}</div>
      <div className="text-sm text-gray-900">{children}</div>
    </div>
  );
}
